import { Theme } from "@mui/material/styles";
import React, { Dispatch, ReactNode, SetStateAction } from "react";
import {
  businessForecastRecordType,
  ForecastExtendedFiltersProps,
  ForecastFiltersProps,
  ForecastPagination,
  ForecastRecord,
  ForecastSortOption,
} from "../../utils/types/Forecast";
import { RequestStatus } from "../../utils/Helpers/fetchStatus";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  createFilterOptions,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Button from "@mui/material/Button";
import { FiltersIcon } from "../../components/Icons/FiltersIcon";
import { SourceIcon } from "../../components/Icons/SourceIcon";
import { LastUpdatedIcon } from "../../components/Icons/LastUpdatedIcon";
import {
  ContractsFiltersType,
  ContractsPagination,
} from "../../slices/contracts";
import { TableProperties } from "exceljs";
import {
  buildExcelTable,
  genEmptyRow,
  getChunksFromString,
  MaxCharsAllowed,
  getOrderedExportColumns,
  mapColumnsToExportFormat,
} from "../../services/exporter";
import {
  removeHtmlTags,
  removeHtmlTagsExceptLink,
} from "../../services/dataParser";
import {
  ColumnsSettingsData,
  DataColumnConfigProps,
} from "../../components/MaterialTable/Table";
import { getContractsColumnsConfig } from "./contractsTableColumns.config";
import axios from "axios/index";
import {
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../../configs/axios-export.custom";
import { buildQuery } from "../../utils/Helpers/queryBuilder";
import { DateFormat, formatDate } from "../../utils/conversion/date-converters";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

export const getSortableColumnPropertyName = (
  _propertyName: string,
): string => {
  return _propertyName === "contactInfo"
    ? "pointOfContactOne"
    : _propertyName === "secondaryContactInfo"
      ? "pointOfContactTwo"
      : _propertyName;
};

const isNumeric = (str: any) => {
  return !isNaN(str) && !isNaN(parseFloat(str));
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

type AgencyFilterKey =
  | "includeUsaid"
  | "includeMcc"
  | "includeStateDept"
  | "includeCdc"
  | "includeDfc"
  | "includeDod"
  | "includeGsa";

const AGENCY_OPTIONS: ReadonlyArray<{
  key: AgencyFilterKey;
  label: string;
  fullName: string;
}> = [
  {
    key: "includeUsaid",
    label: "USAID",
    fullName: "U.S. Agency for International Development",
  },
  {
    key: "includeMcc",
    label: "MCC",
    fullName: "Millennium Challenge Corporation",
  },
  {
    key: "includeStateDept",
    label: "State Dept",
    fullName: "Department of State",
  },
  {
    key: "includeCdc",
    label: "CDC",
    fullName: "Centers for Disease Control and Prevention",
  },
  {
    key: "includeDfc",
    label: "DFC",
    fullName: "U.S. International Development Finance Corporation",
  },
  {
    key: "includeDod",
    label: "DoD",
    fullName: "Department of Defense",
  },
  {
    key: "includeGsa",
    label: "GSA",
    fullName: "General Services Administration",
  },
];

const agencyFilterOptions = createFilterOptions<(typeof AGENCY_OPTIONS)[number]>(
  {
    stringify: (opt) => `${opt.label} ${opt.fullName}`,
  },
);

export const getTableHeader = (
  theme: Theme,
  props: {
    handleOpenFilters: () => void;
    setFilters: Dispatch<SetStateAction<any>>;
    lastUpdated: string | null;
    fetchStatus: string;
    inputValue: string;
    onChange: (event) => void;
    // extendedFilters: ForecastExtendedFiltersProps;
    allFilters: {
      pagination: ContractsPagination;
      filters: ContractsFiltersType;
      sortOption: {
        sortField: string;
        sortOrder: number;
      };
    };
  },
): Array<ReactNode> => {
  const {
    handleOpenFilters,
    setFilters,
    lastUpdated,
    fetchStatus,
    inputValue,
    onChange,
    // extendedFilters,
    allFilters: { pagination, filters, sortOption },
  } = props;

  const isDataFetching = RequestStatus.isFetching(fetchStatus);

  const extendedFiltersLength = Object.entries(filters)
    ?.filter(
      ([key, value]) =>
        !key.includes("include") &&
        !key.includes("sort") &&
        key !== "showAwards" &&
        key !== "onlyUntrackedInPipeline",
    )
    ?.filter(([key, value]) =>
      Array.isArray(value) ? value.length > 0 : !!value,
    )
    ?.map(([key, value]) => key)?.length;

  const topLine = (
    <Box
      sx={{
        pt: 1,
        display: "flex",
        gap: "16px",
        alignItems: "center",
      }}
    >
      {/* todo: temporarily hidden, restore and adjust when possible */}
      <TextField
        value={inputValue}
        onChange={onChange}
        sx={{
          width: "300px",
        }}
        InputProps={{
          startAdornment: (
            <SearchIcon sx={{ color: theme.palette.action.active }} />
          ),
          endAdornment: (
            <>
              {inputValue?.length > 0 && (
                <IconButton
                  onClick={() => onChange({ target: { value: "" } })}
                  sx={{ mr: 0.5 }}
                  disabled={isDataFetching}
                >
                  <ClearIcon />
                </IconButton>
              )}
              <Box
                sx={{
                  borderLeft: `solid 1px ${theme.palette.divider}`,
                  pl: 0.5,
                }}
              >
                <Tooltip title={"Filters"}>
                  <span>
                    <IconButton onClick={handleOpenFilters}>
                      <FiltersIcon sx={{ width: "24px", height: "24px" }} />
                    </IconButton>
                    {extendedFiltersLength > 0 && (
                      <Avatar
                        sx={{
                          position: "absolute",
                          height: "16px",
                          width: "16px",
                          top: "2px",
                          right: "2px",
                          fontSize: "12px",
                          backgroundColor: theme.palette.secondary.main,
                          // border: `solid 1px ${theme.palette.secondary.main}`,
                          pointerEvents: "none",
                        }}
                      >
                        {extendedFiltersLength}
                      </Avatar>
                    )}
                  </span>
                </Tooltip>
              </Box>
            </>
          ),
          sx: {
            px: 0.5,
          },
        }}
        // inputProps={{
        //   style: {
        //     padding: "8px",
        //   },
        // }}
        size={"small"}
        placeholder={"Search"}
        // disabled={isDataFetching}
      />
      {/*<Tooltip title={"Filters"}>*/}
      {/*  <span>*/}
      {/*    <Button*/}
      {/*      variant={"outlined"}*/}
      {/*      endIcon={<FiltersIcon sx={{ width: "24px", height: "24px" }} />}*/}
      {/*      size={"small"}*/}
      {/*      onClick={handleOpenFilters}*/}
      {/*    >*/}
      {/*      Filters*/}
      {/*      {extendedFiltersLength > 0 && (*/}
      {/*        <Avatar*/}
      {/*          sx={{*/}
      {/*            position: "absolute",*/}
      {/*            height: "16px",*/}
      {/*            width: "16px",*/}
      {/*            top: "2px",*/}
      {/*            right: "6px",*/}
      {/*            fontSize: "12px",*/}
      {/*            backgroundColor: theme.palette.secondary.main,*/}
      {/*            // border: `solid 1px ${theme.palette.secondary.main}`,*/}
      {/*            pointerEvents: "none",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          {extendedFiltersLength}*/}
      {/*        </Avatar>*/}
      {/*      )}*/}
      {/*    </Button>*/}
      {/*  </span>*/}
      {/*</Tooltip>*/}
      <FormControlLabel
        onChange={(e, checked) =>
          setFilters((prev) => ({ ...prev, showAwards: checked }))
        }
        control={<Switch checked={filters.showAwards} />}
        label={"Show Awards Only"}
        disabled={isDataFetching}
      />
      <FormControlLabel
        onChange={(e, checked) =>
          setFilters((prev) => ({
            ...prev,
            onlyUntrackedInPipeline: checked,
          }))
        }
        control={<Switch checked={filters.onlyUntrackedInPipeline} />}
        label={"Exclude Tracked Opportunities"}
        disabled={isDataFetching}
      />
    </Box>
  );

  const bottomLine = (
    <Box
      sx={{
        my: 1.5,
        display: "flex",
        gap: "16px",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ minWidth: 480, maxWidth: 600 }}>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={AGENCY_OPTIONS}
          value={AGENCY_OPTIONS.filter((opt) => filters[opt.key])}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          filterOptions={agencyFilterOptions}
          onChange={(_, selected) => {
            const selectedKeys = new Set(selected.map((s) => s.key));
            setFilters((prev) => ({
              ...prev,
              ...AGENCY_OPTIONS.reduce(
                (acc, opt) => ({
                  ...acc,
                  [opt.key]: selectedKeys.has(opt.key),
                }),
                {} as Record<AgencyFilterKey, boolean>,
              ),
            }));
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                sx={{ mr: 1 }}
              />
              {`${option.fullName} (${option.label})`}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agencies"
              placeholder="Select agencies"
            />
          )}
          ChipProps={{
            size: "small",
            sx: {
              "& .MuiChip-deleteIcon": {
                fontSize: "18px !important",
                width: "18px !important",
                height: "18px !important",
              },
            },
          }}
          disabled={isDataFetching}
        />
        {/*<Button*/}
        {/*  variant={"outlined"}*/}
        {/*  endIcon={<FiltersIcon sx={{ width: "24px", height: "24px" }} />}*/}
        {/*  size={"small"}*/}
        {/*  onClick={(e) => {*/}
        {/*    e.preventDefault();*/}
        {/*  }}*/}
        {/*  sx={{*/}
        {/*    opacity: 0,*/}
        {/*    cursor: "initial",*/}
        {/*    mr: 2,*/}
        {/*  }}*/}
        {/*>*/}
        {/*  Filters*/}
        {/*</Button>*/}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          "& > *": { display: "flex", alignItems: "center" },
        }}
      >
        <Typography variant={"subtitle2"} color={"textSecondary"}>
          <SourceIcon
            color={"secondary"}
            sx={{
              height: "16px",
              width: "16px",
              mr: 0.5,
              mt: "-2px",
            }}
          />
          Source: sam.gov
        </Typography>
        <Typography variant={"subtitle2"} color={"textSecondary"}>
          <LastUpdatedIcon
            sx={{
              height: "16px",
              width: "16px",
              mr: 0.5,
              mt: "-2px",
            }}
          />
          Last updated: {lastUpdated ?? "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return [topLine, bottomLine];
};

export const getExportableDataTable = (
  data: any,
  savedColumnSettings?: ColumnsSettingsData[] | null,
): TableProperties => {
  // Get default columns configuration (not details version)
  const defaultColumns = getContractsColumnsConfig(
    getSortableColumnPropertyName,
    false,
  );

  // Get ordered and filtered columns based on user settings
  const orderedColumns = getOrderedExportColumns(
    savedColumnSettings,
    defaultColumns,
  );

  // Define data accessors for each column property
  const dataAccessors: { [propertyName: string]: (record: any) => any } = {
    agencyName: (record) => record.agencyName,
    title: (record) => record.title,
    country: (record) => record.country,
    description: (record) => removeHtmlTags(record.description || ""),
    type: (record) => record.type,
    status: (record) => record.status,
    contractorAwardedName: (record) => record.contractorAwardedName,
    contractorAwardedDate: (record) =>
      record.contractorAwardedDate
        ? record.contractorAwardedDate.toString(DateFormat.dd_MMMM_yyyy)
        : "",
    awardNumber: (record) => record.awardNumber,
    contractorAwardedAmount: (record) =>
      isNumeric(record.contractorAwardedAmount)
        ? currencyFormatter.format(
            parseFloat(record.contractorAwardedAmount || ""),
          )
        : record.contractorAwardedAmount,
    solicitationNumber: (record) => record.solicitationNumber,
    postedDateIn: (record) =>
      formatDate(record.postedDateIn, DateFormat.dd_MMMM_yyyy),
    solicitationDeadlineResponseIn: (record) =>
      formatDate(
        record.solicitationDeadlineResponseIn,
        DateFormat.dd_MMMM_yyyy,
      ),
    modifiedDateIn: (record) =>
      formatDate(record.modifiedDateIn, DateFormat.dd_MMMM_yyyy),
    setAside: (record) => record.setAside,
    classificationCode: (record) => record.classificationCode,
    naicsCodes: (record) => record.naicsCodes,
    placeOfPerformance: (record) => record.placeOfPerformance,
    contactInfo: (record) =>
      record.pointOfContactOne
        ? `${record.pointOfContactOne.fullName}; ${record.pointOfContactOne.email}${
            record.pointOfContactOne.phone
              ? `; ${record.pointOfContactOne.phone}`
              : ""
          }${record.pointOfContactOne.fax ? `; ${record.pointOfContactOne.fax}` : ""}`
        : "",
    secondaryContactInfo: (record) =>
      record.pointOfContactTwo
        ? `${record.pointOfContactTwo.fullName ? record.pointOfContactTwo.fullName + ";" : ""} ${
            record.pointOfContactTwo.email ?? ""
          }${
            record.pointOfContactTwo.phone
              ? `; ${record.pointOfContactTwo.phone}`
              : ""
          }${record.pointOfContactTwo.fax ? `; ${record.pointOfContactTwo.fax}` : ""}`
        : "",
  };

  // Add a special column for Sam.gov link if it's in the ordered columns
  // Check if there's a way to identify link column - for now, prepend it as first column if not explicitly hidden
  const hasSamGovColumn =
    !savedColumnSettings || savedColumnSettings.length === 0;

  let finalColumns = orderedColumns;
  let finalAccessors = { ...dataAccessors };

  // Add Sam.gov link as first column (maintaining backward compatibility)
  if (hasSamGovColumn) {
    finalColumns = [
      {
        propertyName: "samGovLink",
        label: "Sam.gov link",
        order: -1,
        isVisible: true,
        sortable: false,
      } as DataColumnConfigProps,
      ...orderedColumns,
    ];
    finalAccessors["samGovLink"] = (record) =>
      record?.id ? `https://sam.gov/opp/${record?.id}/view` : "";
  }

  // Map columns and data to export format
  const { columns, rows } = mapColumnsToExportFormat(
    finalColumns,
    data,
    finalAccessors,
  );

  return buildExcelTable("ContractsExport", columns, rows);
};

export const getExcelData = async (
  context,
  queryDto,
  totalItems: number = 1000,
): Promise<ForecastRecord[]> => {
  const q = queryDto;
  if (q) {
    q.pageSize = totalItems;
    q.pageIndex = 0;
  }
  let result = Array<any>();
  await axios
    .get(
      getFullUrl(`/api/contract${buildQuery(q, "|")}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    )
    .then((response) => {
      result = response.data.data;
    })
    .catch((error) => {
      alert("error fetching data");
    });
  return result;
};
